import { useCallback, useEffect, useState } from "react";
import { useAuth } from "../../core/auth/AuthContext";
import { useAuthUI } from "../../core/auth/AuthUIContext";
import { getOrganizerInvitation, respondToOrganizerInvitation } from "./organizerInvitation.api";
import CertifiedCelebration from "../../components/ui/CertifiedCelebration";
import { getCertificationKind } from "../certifications/certificationKinds";
import styles from "./OrganizerInvitationPage.module.css";

// Page ouverte depuis un lien /share/organizer-invite/:token (ADR-019) : le membre
// accepte ou refuse le statut « Organisateur certifie ».

function useOrganizerInvitation(token, userId) {
  const [state, setState] = useState({ status: "loading", invitation: null });
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setState((previous) => ({ ...previous, status: "loading" }));
    getOrganizerInvitation(token)
      .then((invitation) => {
        if (!cancelled) setState({ status: invitation ? "ready" : "not-found", invitation });
      })
      .catch((error) => {
        if (!cancelled) setState({ status: error?.status === 404 ? "not-found" : "error", invitation: null });
      });
    return () => { cancelled = true; };
  }, [token, userId, reloadKey]);

  const reload = useCallback(() => setReloadKey((value) => value + 1), []);
  return { ...state, reload };
}

export default function OrganizerInvitationPage({ token }) {
  const { user, loading: isAuthLoading } = useAuth();
  const { openAuthModal } = useAuthUI();
  const userId = user?.id || user?.uid || null;
  const { status, invitation, reload } = useOrganizerInvitation(token, userId);
  const [pendingDecision, setPendingDecision] = useState(null);
  const [submitError, setSubmitError] = useState("");
  const [isCelebrating, setIsCelebrating] = useState(false);

  const respond = async (accept) => {
    setPendingDecision(accept ? "accept" : "decline");
    setSubmitError("");
    try {
      await respondToOrganizerInvitation(token, accept);
      if (accept) setIsCelebrating(true);
      reload();
    } catch (error) {
      setSubmitError(error?.status === 410
        ? "Cette invitation n'est plus valable."
        : "Ta réponse n'a pas pu être enregistrée. Réessaie dans un instant.");
    } finally {
      setPendingDecision(null);
    }
  };

  const viewer = invitation?.viewer || null;
  // Badge propose par le lien (ADR-020) : logo, textes et avantages.
  const certification = getCertificationKind(invitation?.kind);
  const isOpen = invitation?.status === "valid";
  let message = null;
  let showActions = false;

  if (status === "loading" || isAuthLoading) {
    message = <p className={styles.muted}>Chargement de l'invitation…</p>;
  } else if (status === "not-found") {
    message = <p className={styles.muted}>Cette invitation est introuvable.</p>;
  } else if (status === "error") {
    message = (
      <>
        <p className={styles.muted}>Impossible de charger l'invitation pour le moment.</p>
        <button type="button" className={styles.secondaryButton} onClick={reload}>Réessayer</button>
      </>
    );
  } else if (viewer?.decision === "ACCEPTED" || (viewer?.isApproved && viewer?.decision !== "DECLINED")) {
    message = <p className={styles.success}>Bienvenue parmi les {certification.celebration.groupLabel.toLowerCase()} ! Ton badge est actif.</p>;
  } else if (!isOpen) {
    message = <p className={styles.muted}>Cette invitation n'est plus valable. Demande un nouveau lien à KARANGO.</p>;
  } else if (!userId) {
    message = (
      <button type="button" className={styles.primaryButton} onClick={() => openAuthModal?.()}>
        Se connecter pour répondre
      </button>
    );
  } else {
    if (viewer?.decision === "DECLINED") {
      message = <p className={styles.muted}>Tu as refusé cette invitation. Tu peux encore changer d'avis.</p>;
    }
    showActions = true;
  }

  return (
    <main className={styles.page}>
      <section className={styles.card}>
        <img className={styles.logo} src={certification.logo} alt="" width="112" height="112" />
        <p className={styles.kicker}>Invitation KARANGO</p>
        <h1 className={styles.title}>{certification.invitation.title}</h1>
        <p className={styles.lead}>{certification.invitation.lead}</p>
        <h2 className={styles.perksTitle}>Ce que tu pourras faire</h2>
        <ul className={styles.perks}>
          {certification.invitation.perks.map((perk) => (
            <li key={perk.id} className={styles.perk}>
              <span className={styles.perkIcon} aria-hidden="true">{perk.icon}</span>
              <span>
                <strong className={styles.perkTitle}>{perk.title}</strong>
                <span className={styles.perkText}>{perk.description}</span>
              </span>
            </li>
          ))}
        </ul>
        <p className={styles.perksNote}>Tu gardes bien sûr tout ce que tu fais déjà sur KARANGO.</p>
        <a className={styles.homeLink} href="/">Ouvrir Karango</a>
        {/* Zone de reponse fixee en bas de l'ecran : toujours visible pendant le defilement. */}
        <div className={styles.responseBar}>
          {message}
          {showActions && (
            <div className={styles.actions}>
              <button type="button" className={styles.primaryButton} disabled={Boolean(pendingDecision)} onClick={() => respond(true)}>
                {pendingDecision === "accept" ? "Un instant…" : "Accepter"}
              </button>
              <button type="button" className={styles.secondaryButton} disabled={Boolean(pendingDecision)} onClick={() => respond(false)}>
                {pendingDecision === "decline" ? "Un instant…" : "Refuser"}
              </button>
            </div>
          )}
          {submitError && <p className={styles.error} role="alert">{submitError}</p>}
        </div>
      </section>
      {isCelebrating && (
        <CertifiedCelebration
          kind={certification.kind}
          displayName={user?.username || user?.displayName}
          homeHref="/"
          onClose={() => setIsCelebrating(false)}
        />
      )}
    </main>
  );
}
